// Live session updates: owns the socket's session-room subscription so the
// page never ends up connected-but-deaf.
//
// Why this exists: socket.io reconnects on its own, but the SERVER forgets
// room membership on every disconnect (a blip, the polling→websocket
// upgrade on first load, a deploy). The page used to emit joinSession once
// on mount, so after any reconnect it silently stopped receiving
// state_updates. Here, every (re)connect re-authenticates and re-joins the
// current room and waits for the server's ack; a failed or unanswered join
// retries with backoff; after a reconnect (or returning to the tab) the
// caller's resync runs, re-fetching over HTTP to catch updates missed while
// disconnected. Presence heartbeats keep web users "online" like the app.
//
// `liveStatus.state`: connecting | live | reconnecting | offline | denied.
//
// Auth: the JWT rides the handshake (main.js). The server can answer with a
// fresh token (`token_refresh` event, or `token_refresh` in the authenticate
// ack) when it verified a migrated token through its fallback key, and it
// refuses the handshake (connect_error 'auth-invalid' / 'auth-required') when
// a presented token no longer verifies. Both are handed to the callbacks
// main.js passes in; the decisions live in services/tokenRefresh.js.
import { reactive } from "vue";
import { authRefusalKind } from "@/services/tokenRefresh";

export const liveStatus = reactive({
  state: "connecting",
  room: null, // session name we want live updates for
  lastUpdateAt: null, // last state_update received (ms)
});

const JOIN_TIMEOUT_MS = 6000;
const HEARTBEAT_MS = 20000;
const RETRY_MAX_MS = 15000;

let socket = null;
let getUser = () => null;
let hooks = {}; // { onTokenRefresh(token), onAuthRefused(kind, err) }
let desired = null; // { name, onResync }
let joinSeq = 0;
let retryTimer = null;
let retryDelay = 2000;

function setState(state) {
  liveStatus.state = state;
}

function offline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function authenticate() {
  const user = getUser();
  if (!user || !socket) return;
  // A server that verified this handshake through the fallback key also puts
  // the replacement token in the ack (older servers never ack: harmless).
  socket.emit("authenticate", { username: user.username }, (ack) => {
    if (ack && ack.token_refresh) hooks.onTokenRefresh?.(ack.token_refresh);
  });
}

function scheduleRetry() {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    if (desired && socket && socket.connected) join(true);
  }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
}

// Join `desired` and wait for the ack. `resync` = also re-fetch afterwards
// (anything could have changed while we weren't in the room).
function join(resync) {
  const target = desired;
  if (!target || !socket) return;
  const seq = ++joinSeq;
  const username = getUser()?.username || null;
  if (liveStatus.state !== "live") setState("connecting");
  socket
    .timeout(JOIN_TIMEOUT_MS)
    .emit("joinSession", { username, session_name: target.name }, (err, ack) => {
      if (seq !== joinSeq || desired !== target) return; // superseded
      if (err) {
        // No answer in time: the socket is up but the join didn't land.
        setState(socket.connected ? "reconnecting" : offline() ? "offline" : "reconnecting");
        scheduleRetry();
        return;
      }
      if (ack && ack.ok === false) {
        if (ack.error === "private") {
          setState("denied");
        } else {
          setState("reconnecting");
          scheduleRetry();
        }
        return;
      }
      // ok (an older server answers with no payload — it still answered).
      retryDelay = 2000;
      clearTimeout(retryTimer);
      setState("live");
      // A signed-in join makes the server broadcast fresh state to the room;
      // an anonymous listener (or a rejoin) needs to fetch it.
      if (resync || !username) target.onResync?.();
    });
}

function heartbeat() {
  const user = getUser();
  if (!socket?.connected || !desired || !user) return;
  socket.emit("presenceHeartbeat", { username: user.username, session_name: desired.name });
}

/** Wire the shared socket once, at app start. */
export function initLiveSocket(sock, userGetter, callbacks = {}) {
  socket = sock;
  getUser = userGetter;
  hooks = callbacks;

  sock.on("connect", () => {
    authenticate();
    if (desired) join(true);
    else setState("live");
  });
  sock.on("disconnect", (reason) => {
    setState(offline() ? "offline" : "reconnecting");
    // The server kicked us (e.g. a restart): socket.io won't auto-retry that.
    if (reason === "io server disconnect") sock.connect();
  });
  sock.on("connect_error", (err) => {
    const refusal = authRefusalKind(err);
    if (refusal) {
      // Refused by the server: socket.io won't retry; the caller decides
      // (drop the token, prompt a re-login, reconnect anonymously).
      setState(refusal === "auth-required" ? "denied" : "reconnecting");
      hooks.onAuthRefused?.(refusal, err);
      return;
    }
    setState(offline() ? "offline" : "reconnecting");
  });
  sock.on("token_refresh", (payload) => {
    if (payload && payload.token) hooks.onTokenRefresh?.(payload.token);
  });
  sock.on("state_update", () => {
    liveStatus.lastUpdateAt = Date.now();
  });

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      if (!sock.connected) sock.connect();
    });
    window.addEventListener("offline", () => setState("offline"));
    // Background tabs get throttled and sockets silently die; coming back
    // re-checks the connection and the room, and catches up.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      if (!sock.connected) sock.connect();
      else if (desired) join(true);
    });
  }
  setInterval(heartbeat, HEARTBEAT_MS);

  if (sock.connected) setState("live");
}

/** Receive live updates for `name`. `onResync` re-fetches the session. */
export function joinSessionRoom(name, onResync) {
  if (!socket || !name) return;
  if (desired && desired.name !== name) leaveSessionRoom(desired.name);
  desired = { name, onResync };
  liveStatus.room = name;
  retryDelay = 2000;
  if (socket.connected) join(false);
  else setState(offline() ? "offline" : "connecting");
}

/** Stop receiving updates for `name` (no-op if it isn't the current room). */
export function leaveSessionRoom(name) {
  if (!desired || desired.name !== name) return;
  clearTimeout(retryTimer);
  joinSeq += 1; // drop any in-flight join ack
  if (socket?.connected) {
    socket.emit("leaveSession", {
      username: getUser()?.username || null,
      session_name: name,
    });
  }
  desired = null;
  liveStatus.room = null;
}

/** Log in / log out changed who this socket is: redo the handshake so the
 *  server sees the new token (rooms are re-joined on the reconnect). */
export function refreshSocketAuth() {
  if (!socket) return;
  if (socket.connected) socket.disconnect();
  socket.connect();
}

/** The status chip's "retry": reconnect or re-join right now. */
export function reconnectNow() {
  if (!socket) return;
  retryDelay = 2000;
  if (!socket.connected) {
    setState("connecting");
    socket.connect();
  } else if (desired) {
    join(true);
  }
}

/** Listen for a server event on the shared socket (the shell's Inbox badge:
 *  invitation_received, invitation_accepted). Returns the unsubscribe. */
export function onSocket(event, fn) {
  if (!socket || !event || typeof fn !== "function") return () => {};
  socket.on(event, fn);
  return () => socket && socket.off(event, fn);
}
